import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../../config/apiConfig';
import {
  fetchDeviationReferenceDocument,
  formatDeviationReferenceDocumentText,
} from '../utils/fetchDeviationReferenceDocument';

/**
 * DeviationReport - Printable report component for deviations
 * Displays all deviation information in a print-friendly format
 */
const DeviationReport = ({ deviation }) => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoFetchAttempted, setLogoFetchAttempted] = useState(false);
  const [referenceDocument, setReferenceDocument] = useState(null);
  const [referenceDocLoading, setReferenceDocLoading] = useState(true);

  // Fetch company logo from application settings
  useEffect(() => {
    if (logoFetchAttempted) return;
    
    const fetchCompanyLogo = async () => {
      setLogoFetchAttempted(true);
      
      try {
        const token = localStorage.getItem('authToken');
        const url = `${API_BASE_URL}/application-setting/key/companyLogo`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });

        if (response.ok) {
          const setting = await response.json();
          
          if (setting && setting.value && setting.isActive !== false) {
            let logoUrl = setting.value.trim();
            
            if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
              const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
              logoUrl = logoUrl.startsWith('/') 
                ? `${baseUrl}${logoUrl}` 
                : `${baseUrl}/${logoUrl}`;
            }
            
            setCompanyLogo(logoUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching company logo:', error);
      }
    };

    fetchCompanyLogo();
  }, [logoFetchAttempted]);

  useEffect(() => {
    let cancelled = false;

    const loadReferenceDocument = async () => {
      // Prefer snapshot stored on the deviation at creation time
      if (deviation?.referenceDocument) {
        setReferenceDocument({ snapshotText: deviation.referenceDocument });
        setReferenceDocLoading(false);
        return;
      }

      setReferenceDocLoading(true);
      try {
        const doc = await fetchDeviationReferenceDocument();
        if (!cancelled) setReferenceDocument(doc);
      } catch {
        if (!cancelled) setReferenceDocument(null);
      } finally {
        if (!cancelled) setReferenceDocLoading(false);
      }
    };

    loadReferenceDocument();
    return () => {
      cancelled = true;
    };
  }, [deviation?.id, deviation?.referenceDocument]);

  if (!deviation) {
    return <div>No deviation data available</div>;
  }

  // Helper functions
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return dayjs(date).format('DD/MM/YYYY HH:mm');
    } catch {
      return date;
    }
  };

  const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return date;
    }
  };

  const formatBoolean = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'N/A';
  };

  const formatArray = (arr, field = 'name') => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return 'N/A';
    return arr.map(item => item[field] || item.name || item).join(', ');
  };

  return (
    <div className="deviation-report print-only" style={styles.container}>
      <style>{printStyles}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            {companyLogo && (
              <img 
                src={companyLogo} 
                alt="Company Logo" 
                style={styles.logo}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </div>
          <div style={styles.headerRight}>
            <h1 style={styles.title}>DEVIATION REPORT</h1>
            <div style={styles.referenceDocument}>
              {referenceDocLoading ? (
                <span style={styles.referenceDocumentMuted}>Loading…</span>
              ) : (
                referenceDocument?.snapshotText ||
                formatDeviationReferenceDocumentText(referenceDocument) || (
                  <span style={styles.referenceDocumentMuted}>N/A</span>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>1. BASIC INFORMATION</h2>
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '38%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Deviation Code:</td>
              <td style={styles.valueCell}><strong>{deviation.code || 'N/A'}</strong></td>
              <td style={styles.labelCell}>Status:</td>
              <td style={styles.valueCell}><strong>{deviation.status?.name || 'N/A'}</strong></td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Title:</td>
              <td style={styles.valueCell} colSpan="3">{deviation.title || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Description:</td>
              <td style={styles.valueCell} colSpan="3">{deviation.description || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Discovery & Reporting */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>2. DISCOVERY & REPORTING</h2>
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '40%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Discovered Date:</td>
              <td style={styles.valueCell}>{formatDate(deviation.discoveredAt)}</td>
              <td style={styles.labelCell}>Discovered By Depositor:</td>
              <td style={styles.valueCell}>{deviation.discoveredBy?.name || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Reported Date:</td>
              <td style={styles.valueCell}>{formatDate(deviation.reportedAt)}</td>
              <td style={styles.labelCell}>Reported By:</td>
              <td style={styles.valueCell}>
                {deviation.reportedByName ||
                  deviation.reportedBy?.userDisplayName ||
                  deviation.reportedBy?.name ||
                  'N/A'}
              </td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Due Date:</td>
              <td style={styles.valueCell}>{formatDateOnly(deviation.dueDate)}</td>
              <td style={styles.labelCell}></td>
              <td style={styles.valueCell}></td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Products */}
      {deviation.products && deviation.products.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. PRODUCTS</h2>
          <table style={styles.fullTable}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Product Code</th>
                <th style={styles.tableHeader}>Product Name</th>
                <th style={styles.tableHeader}>Batch No.</th>
                <th style={styles.tableHeader}>Quantity</th>
                <th style={styles.tableHeader}>Expiry Date</th>
              </tr>
            </thead>
            <tbody>
              {deviation.products.map((product, index) => (
                <tr key={index}>
                  <td style={styles.tableCell}>{product.productCode || 'N/A'}</td>
                  <td style={styles.tableCell}>{product.productName || 'N/A'}</td>
                  <td style={styles.tableCell}>{product.batchNumber || 'N/A'}</td>
                  <td style={styles.tableCell}>{product.productQuantity || 'N/A'}</td>
                  <td style={styles.tableCell}>{formatDateOnly(product.expiryDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Summary of Investigation Results */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>4. SUMMARY OF INVESTIGATION RESULTS INTO THE CAUSES OF THE DEVIATION</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Cause Categorization:</td>
              <td style={styles.valueCell} colSpan="3">{formatArray(deviation.rootCauses)}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Cause Categorization Note:</td>
              <td style={styles.valueCell} colSpan="3">{deviation.rootCauseNote || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Department Head Date:</td>
              <td style={styles.valueCell}>{formatDateOnly(deviation.departmentHeadInvestigation)}</td>
              <td style={styles.labelCell}>Department Head By:</td>
              <td style={styles.valueCell}>{deviation.departmentHeadInvestigationBy?.userDisplayName || deviation.departmentHeadInvestigationBy?.name || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>QA Manager Date:</td>
              <td style={styles.valueCell}>{formatDateOnly(deviation.qaManagerInvestigation)}</td>
              <td style={styles.labelCell}>QA Manager By:</td>
              <td style={styles.valueCell}>{deviation.qaManagerInvestigationBy?.userDisplayName || deviation.qaManagerInvestigationBy?.name || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Assessment of Criticality and Impact */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>5. ASSESSMENT OF CRITICALITY AND IMPACT</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Criticality Categorization:</td>
              <td style={styles.valueCell}>{deviation.criticality?.name || 'N/A'}</td>
              <td style={styles.labelCell}></td>
              <td style={styles.valueCell}></td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Notification of Customer:</td>
              <td style={styles.valueCell}>{formatBoolean(deviation.customerImpact)}</td>
              <td style={styles.labelCell}>Note:</td>
              <td style={styles.valueCell}>{deviation.customerImpactNote || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Impact on Product:</td>
              <td style={styles.valueCell}>{formatBoolean(deviation.impactOnProduct)}</td>
              <td style={styles.labelCell}>Note:</td>
              <td style={styles.valueCell}>{deviation.impactOnProductNote || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Impact on Other Batches:</td>
              <td style={styles.valueCell}>{formatBoolean(deviation.impactOnOtherBatches)}</td>
              <td style={styles.labelCell}>Note:</td>
              <td style={styles.valueCell}>{deviation.impactOnOtherBatchesNote || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Impact on Other Products:</td>
              <td style={styles.valueCell}>{formatBoolean(deviation.impactOnOtherProducts)}</td>
              <td style={styles.labelCell}>Note:</td>
              <td style={styles.valueCell}>{deviation.impactOnOtherProductsNote || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Impact on Other Systems:</td>
              <td style={styles.valueCell}>{formatBoolean(deviation.impactOnOtherSystems)}</td>
              <td style={styles.labelCell}>Note:</td>
              <td style={styles.valueCell}>{deviation.impactOnOtherSystemsNote || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Final Product Disposition */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>6. FINAL PRODUCT DISPOSITION (FOR PRODUCT-RELATED DEVIATIONS)</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Final Disposition:</td>
              <td style={styles.valueCell}>{deviation.finalDisposition?.name || 'N/A'}</td>
              <td style={styles.labelCell}></td>
              <td style={styles.valueCell}></td>
            </tr>
            {deviation.finalDispositionOtherNote && (
              <tr>
                <td style={styles.labelCell}>Other Note:</td>
                <td style={styles.valueCell} colSpan="3">{deviation.finalDispositionOtherNote}</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Risk Assessment */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>7. RISK ASSESSMENT</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Risk Assessment Required:</td>
              <td style={styles.valueCell}>{formatBoolean(deviation.riskAssessmentRequired)}</td>
              <td style={styles.labelCell}>Risk Assessment:</td>
              <td style={styles.valueCell}>{deviation.riskAssessment?.code || deviation.riskAssessment?.title || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>RA Completed At:</td>
              <td style={styles.valueCell}>{formatDate(deviation.riskAssessmentCompletedAt)}</td>
              <td style={styles.labelCell}></td>
              <td style={styles.valueCell}></td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Corrective and Preventive Actions (CAPA) */}
      {deviation.deviationActions && deviation.deviationActions.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. CORRECTIVE AND PREVENTIVE ACTIONS (CAPA)</h2>
          <table style={styles.fullTable}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Action Type</th>
                <th style={styles.tableHeader}>Description</th>
                <th style={styles.tableHeader}>Responsible</th>
                <th style={styles.tableHeader}>Due Date</th>
                <th style={styles.tableHeader}>Close Date</th>
              </tr>
            </thead>
            <tbody>
              {deviation.deviationActions.map((action, index) => (
                <tr key={index}>
                  <td style={styles.tableCell}>{action.actionType?.name || 'N/A'}</td>
                  <td style={styles.tableCell}>{action.description || 'N/A'}</td>
                  <td style={styles.tableCell}>{action.responsible || 'N/A'}</td>
                  <td style={styles.tableCell}>{formatDateOnly(action.dueDate)}</td>
                  <td style={styles.tableCell}>{formatDateOnly(action.closeDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Implementation */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>9. IMPLEMENTATION</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>All required actions have been completed:</td>
              <td style={styles.valueCell}>{formatBoolean(deviation.implementation)}</td>
              <td style={styles.labelCell}>Implementation Completed At:</td>
              <td style={styles.valueCell}>{formatDate(deviation.implementationCompletedAt)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Implementation Review Required */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>10. IMPLEMENTATION REVIEW REQUIRED</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Implementation Review Required:</td>
              <td style={styles.valueCell}>{formatBoolean(deviation.implementationEvaluation)}</td>
              <td style={styles.labelCell}>Implementation Evaluation At:</td>
              <td style={styles.valueCell}>{formatDate(deviation.implementationEvaluationAt)}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Implementation Evaluation Note:</td>
              <td style={styles.valueCell} colSpan="3">{deviation.implementationEvaluationNote || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Implementation Evaluation By:</td>
              <td style={styles.valueCell}>{deviation.implementationEvaluationBy?.userDisplayName || deviation.implementationEvaluationBy?.name || 'N/A'}</td>
              <td style={styles.labelCell}></td>
              <td style={styles.valueCell}></td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Implementation Evaluation (ΠΑΡ.2) */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>11. IMPLEMENTATION EVALUATION</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Required:</td>
              <td style={styles.valueCell} colSpan="3">
                {deviation.implementationEvaluationRequiredMonths
                  ? `${deviation.implementationEvaluationRequiredMonths} months`
                  : 'N/A'}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Closure */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>12. CLOSURE</h2>
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.labelCell}>Closed At:</td>
              <td style={styles.valueCell}>{formatDate(deviation.closedAt)}</td>
              <td style={styles.labelCell}>Closed By:</td>
              <td style={styles.valueCell}>{deviation.closedBy?.userDisplayName || deviation.closedBy?.name || 'N/A'}</td>
            </tr>
            <tr>
              <td style={styles.labelCell}>Closure Comments:</td>
              <td style={styles.valueCell} colSpan="3">{deviation.closureComments || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.footerContent}>
          <p style={styles.footerText}>
            This report was generated on {dayjs().format('DD/MM/YYYY HH:mm')} from the Quality Management System.
          </p>
          <p style={styles.footerText}>
            Deviation Code: <strong>{deviation.code}</strong> | ID: {deviation.id}
          </p>
        </div>
      </div>
    </div>
  );
};

// Styles (same as ComplaintReport)
const styles = {
  container: {
    maxWidth: '210mm',
    margin: '0 auto',
    padding: '20mm',
    backgroundColor: '#fff',
    fontFamily: 'Arial, sans-serif',
    fontSize: '11pt',
    lineHeight: '1.4',
    color: '#000',
  },
  header: {
    borderBottom: '3px solid #000',
    paddingBottom: '10px',
    marginBottom: '20px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'flex-start',
  },
  headerRight: {
    flex: '1 1 auto',
    textAlign: 'right',
  },
  logo: {
    maxHeight: '50px',
    maxWidth: '200px',
    height: 'auto',
    width: 'auto',
    objectFit: 'contain',
    marginRight: '20px',
  },
  title: {
    fontSize: '24pt',
    fontWeight: 'bold',
    margin: 0,
    color: '#000',
    marginBottom: '5px',
  },
  referenceDocument: {
    fontSize: '11pt',
    margin: 0,
    marginBottom: '5px',
    color: '#000',
    textAlign: 'right',
  },
  referenceDocumentMuted: {
    fontStyle: 'italic',
    color: '#666',
  },
  section: {
    marginBottom: '20px',
    pageBreakInside: 'auto',
  },
  sectionTitle: {
    fontSize: '14pt',
    fontWeight: 'bold',
    marginBottom: '10px',
    paddingBottom: '5px',
    borderBottom: '2px solid #000',
    color: '#000',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '10px',
  },
  fullTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '10px',
    border: '1px solid #000',
  },
  labelCell: {
    padding: '8px',
    fontWeight: 'bold',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    verticalAlign: 'top',
  },
  valueCell: {
    padding: '8px',
    border: '1px solid #ddd',
    verticalAlign: 'top',
  },
  tableHeader: {
    padding: '10px',
    backgroundColor: '#333',
    color: '#fff',
    fontWeight: 'bold',
    textAlign: 'left',
    border: '1px solid #000',
  },
  tableCell: {
    padding: '8px',
    border: '1px solid #ddd',
    verticalAlign: 'top',
  },
  footer: {
    marginTop: '30px',
    paddingTop: '10px',
    borderTop: '2px solid #000',
    textAlign: 'center',
    fontSize: '9pt',
    color: '#666',
  },
  footerContent: {
    marginTop: '10px',
  },
  footerText: {
    margin: '5px 0',
  },
};

// Print-specific styles (same as ComplaintReport)
const printStyles = `
  @media print {
    @page {
      size: A4;
      margin: 15mm;
    }
    
    .no-print {
      display: none !important;
    }
    
    header, nav, aside, .sidebar, .main-header, .app-header,
    [class*="header"]:not(.deviation-report), [class*="nav"], [class*="sidebar"] {
      display: none !important;
    }
    
    .deviation-report-wrapper {
      display: block !important;
      visibility: visible !important;
      width: 100% !important;
      height: auto !important;
    }
    
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      width: 100% !important;
    }
    
    .deviation-report {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 20mm !important;
      box-shadow: none !important;
      height: auto !important;
      overflow: visible !important;
      page-break-after: auto;
      position: relative !important;
      background: white !important;
      display: block !important;
      visibility: visible !important;
    }
    
    .no-print {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    section {
      page-break-inside: auto;
      page-break-after: auto;
      margin-bottom: 15px;
      overflow: visible !important;
    }
    
    table {
      page-break-inside: auto;
      border-collapse: collapse;
      width: 100% !important;
    }
    
    thead {
      display: table-header-group;
    }
    
    tbody {
      display: table-row-group;
    }
    
    tr {
      page-break-inside: auto;
      page-break-after: auto;
    }
    
    h2 {
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    
    td, th {
      page-break-inside: auto;
    }
    
    p, div {
      orphans: 3;
      widows: 3;
    }
    
    * {
      visibility: visible !important;
    }
  }
  
  @media screen {
    .deviation-report {
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
      margin: 20px auto;
    }
  }
`;

export default DeviationReport;

